import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Users, Crown, ShieldCheck, Trash, MagnifyingGlass, ArrowClockwise,
  Package, MapPin, CheckCircle, X, Sparkle, Gift,
} from "@phosphor-icons/react";
import AppLayout from "@/components/AppLayout";
import { api, formatError } from "@/lib/api";
import { toast } from "sonner";

const TIER_META = {
  free:   { label: "Free",   color: "bg-sand-200 text-stoneink-700 border-sand-300" },
  pro:    { label: "Pro",    color: "bg-sage-400/15 text-sage-600 border-sage-400/40" },
  family: { label: "Family", color: "bg-terracotta-50 text-terracotta-600 border-terracotta-500" },
};

export default function UsersAdmin() {
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [tier, setTier] = useState("all");
  const [role, setRole] = useState("all");

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (search.trim()) params.search = search.trim();
      if (tier !== "all") params.tier = tier;
      if (role !== "all") params.role = role;
      const [{ data: u }, { data: s }] = await Promise.all([
        api.get("/admin/users", { params }),
        api.get("/admin/users/stats"),
      ]);
      setUsers(u || []);
      setStats(s);
    } catch (err) {
      toast.error(formatError(err) || "Could not load users");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tier, role]);
  useEffect(() => {
    const reload = () => load();
    window.addEventListener("nestled-reload-users", reload);
    return () => window.removeEventListener("nestled-reload-users", reload);
    /* eslint-disable-next-line */
  }, [tier, role, search]);

  const onSearchKey = (e) => { if (e.key === "Enter") load(); };

  const deleteUser = async (u) => {
    const msg = `Delete ${u.email}?\n\nThis permanently removes:\n• Their profile\n• ${u.item_count} item(s) and ${u.location_count} location(s)\n• Their household${u.is_household_owner ? " (and removes all members)" : ""}\n• Billing history is anonymized for 7-year tax retention\n\nThis cannot be undone.`;
    if (!window.confirm(msg)) return;
    try {
      await api.delete(`/admin/users/${u.id}`);
      toast.success(`Deleted ${u.email}`);
      setUsers((rows) => rows.filter((r) => r.id !== u.id));
      // refresh stats so the totals stay accurate
      api.get("/admin/users/stats").then(({ data }) => setStats(data)).catch(() => {});
    } catch (err) {
      toast.error(formatError(err));
    }
  };

  return (
    <AppLayout>
      <div className="space-y-6" data-testid="users-admin-page">
        <header className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.2em] text-terracotta-600 mb-1">
              <Users weight="duotone" size={14} /> Admin · Users
            </div>
            <h1 className="font-heading text-3xl sm:text-4xl text-stoneink-900 font-semibold tracking-tight">
              All Nestled users
            </h1>
            <p className="mt-2 text-stoneink-700">View every account, see plan, household, and inventory size — and delete users who need to go.</p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={load} data-testid="users-admin-refresh"
              className="h-10 px-4 rounded-xl bg-white border border-sand-300 text-stoneink-700 hover:bg-sand-100 text-sm font-medium flex items-center gap-2">
              <ArrowClockwise size={14} /> Refresh
            </button>
          </div>
        </header>

        <DemoControlPanel />

        {/* Stats cards */}
        {stats && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="users-admin-stats">
            <StatCard icon={Users} label="Total users" value={stats.total_users}
              sub={`+${stats.new_last_7d} this week`} />
            <StatCard icon={Crown} label="Family / Pro" value={stats.by_tier.family + stats.by_tier.pro}
              sub={`${stats.by_tier.family} family · ${stats.by_tier.pro} pro`} />
            <StatCard icon={Package} label="Items" value={stats.items}
              sub={`${stats.locations} locations`} />
            <StatCard icon={ShieldCheck} label="Households" value={stats.households}
              sub={`${stats.admins} admin · ${stats.new_last_30d} new (30d)`} />
          </div>
        )}

        {/* Filters */}
        <div className="rounded-2xl bg-white border border-sand-300 p-4 flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[200px]">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-stoneink-500" />
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={onSearchKey}
              placeholder="Search by name or email… (press Enter)"
              data-testid="users-admin-search"
              className="w-full h-10 pl-9 pr-3 rounded-xl bg-sand-100 border border-sand-300 focus:border-terracotta-500 outline-none text-sm"
            />
          </div>
          <Select value={tier} onChange={setTier} options={[
            ["all","All tiers"], ["free","Free"], ["pro","Pro"], ["family","Family"],
          ]} testid="users-admin-tier-filter" />
          <Select value={role} onChange={setRole} options={[
            ["all","All roles"], ["user","User"], ["admin","Admin"],
          ]} testid="users-admin-role-filter" />
          {(search || tier !== "all" || role !== "all") && (
            <button onClick={() => { setSearch(""); setTier("all"); setRole("all"); }}
              className="h-10 px-3 rounded-xl text-stoneink-500 hover:text-stoneink-900 text-sm flex items-center gap-1">
              <X size={14} /> Clear
            </button>
          )}
        </div>

        {/* Table */}
        <div className="rounded-2xl bg-white border border-sand-300 overflow-hidden">
          {loading ? (
            <div className="p-12 text-center text-stoneink-500">Loading…</div>
          ) : users.length === 0 ? (
            <div className="p-12 text-center text-stoneink-500">No users match the current filters.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-sand-100 text-stoneink-700">
                  <tr>
                    <th className="text-left p-3 font-medium">User</th>
                    <th className="text-left p-3 font-medium">Plan</th>
                    <th className="text-left p-3 font-medium">Household</th>
                    <th className="text-left p-3 font-medium">Inventory</th>
                    <th className="text-left p-3 font-medium">Joined</th>
                    <th className="text-right p-3 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => <UserRow key={u.id} u={u} onDelete={deleteUser} />)}
                </tbody>
              </table>
            </div>
          )}
          {!loading && users.length > 0 && (
            <div className="px-4 py-3 border-t border-sand-300 text-xs text-stoneink-500 text-right">
              Showing {users.length} {users.length === 1 ? "user" : "users"}
            </div>
          )}
        </div>
      </div>
    </AppLayout>
  );
}

const StatCard = ({ icon: Icon, label, value, sub }) => (
  <div className="rounded-2xl bg-white border border-sand-300 p-4 shadow-ambient">
    <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.16em] text-stoneink-500 mb-2">
      <Icon weight="duotone" size={14} className="text-terracotta-600" /> {label}
    </div>
    <div className="font-heading text-2xl font-semibold text-stoneink-900">{value}</div>
    {sub && <div className="text-xs text-stoneink-500 mt-1">{sub}</div>}
  </div>
);

const Select = ({ value, onChange, options, testid }) => (
  <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
    className="h-10 px-3 rounded-xl bg-sand-100 border border-sand-300 text-sm text-stoneink-700 focus:border-terracotta-500 outline-none">
    {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
  </select>
);

const UserRow = ({ u, onDelete }) => {
  const tierMeta = TIER_META[u.tier] || TIER_META.free;
  return (
    <tr className="border-t border-sand-300 hover:bg-sand-100/50" data-testid={`users-row-${u.id}`}>
      <td className="p-3">
        <div className="font-medium text-stoneink-900 flex items-center gap-2">
          {u.name || <span className="italic text-stoneink-500">(no name)</span>}
          {u.role === "admin" && (
            <span className="px-1.5 py-0.5 rounded-full bg-terracotta-500 text-white text-[9px] font-bold uppercase tracking-[0.14em]">Admin</span>
          )}
          {u.is_legacy_mobile && (
            <span className="px-1.5 py-0.5 rounded-full bg-sand-200 text-stoneink-700 text-[9px] font-medium">Mobile migrated</span>
          )}
        </div>
        <div className="text-xs text-stoneink-500">{u.email}</div>
      </td>
      <td className="p-3">
        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs font-medium ${tierMeta.color}`}>
          <Sparkle weight="duotone" size={12} /> {tierMeta.label}
        </span>
        {u.tier !== "free" && u.plan_cancelled && (
          <div className="text-[10px] text-ochre-600 mt-1">Cancelled — ends {fmt(u.plan_expires_at)}</div>
        )}
        {u.tier_source === "admin" && (
          <div className="text-[10px] text-stoneink-500 mt-1">Lifetime (admin)</div>
        )}
        {u.tier_source === "household" && (
          <div className="text-[10px] text-stoneink-500 mt-1">Inherited</div>
        )}
      </td>
      <td className="p-3 text-stoneink-700 text-xs">
        {u.household_name ? (
          <>
            <div className="font-medium">{u.household_name}</div>
            {u.is_household_owner && <div className="text-[10px] text-terracotta-600 font-medium">Owner</div>}
          </>
        ) : <span className="text-stoneink-500">—</span>}
      </td>
      <td className="p-3 text-stoneink-700 text-xs">
        <div className="flex items-center gap-1"><Package size={12} /> {u.item_count} items</div>
        <div className="flex items-center gap-1 mt-0.5"><MapPin size={12} /> {u.location_count} locations</div>
      </td>
      <td className="p-3 text-stoneink-500 text-xs">{fmt(u.created_at)}</td>
      <td className="p-3 text-right whitespace-nowrap">
        <CompMenu u={u} />
        <button onClick={() => onDelete(u)} data-testid={`users-row-delete-${u.id}`}
          disabled={u.role === "admin"}
          title={u.role === "admin" ? "Cannot delete admin accounts" : "Delete user"}
          className="h-8 w-8 rounded-lg text-stoneink-500 hover:bg-destructive/10 hover:text-destructive inline-flex items-center justify-center disabled:opacity-30 disabled:cursor-not-allowed ml-1">
          <Trash size={14} />
        </button>
      </td>
    </tr>
  );
};

function CompMenu({ u }) {
  const [open, setOpen] = useState(false);
  const [tier, setTier] = useState(u.tier === "free" ? "family" : u.tier);
  const [duration, setDuration] = useState("lifetime"); // "30","90","365","lifetime"
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (overrideTier) => {
    setBusy(true);
    try {
      const payload = {
        tier: overrideTier || tier,
        duration_days: duration === "lifetime" ? null : parseInt(duration, 10),
        note,
      };
      await api.post(`/admin/users/${u.id}/comp`, payload);
      toast.success(payload.tier === "free"
        ? `Comp revoked from ${u.email}`
        : `Granted ${payload.tier} ${duration === "lifetime" ? "(lifetime)" : `for ${duration} days`}`);
      setOpen(false);
      // Reload after a tick so the row shows updated tier
      window.dispatchEvent(new Event("nestled-reload-users"));
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setBusy(false);
    }
  };

  if (u.role === "admin") return null;

  return (
    <span className="relative inline-block">
      <button onClick={() => setOpen((v) => !v)}
        data-testid={`users-row-comp-${u.id}`}
        title="Grant or revoke comp tier"
        className={`h-8 px-2.5 rounded-lg text-xs font-medium inline-flex items-center gap-1.5 mr-1 ${
          u.tier_source === "comp"
            ? "bg-sage-400/15 text-sage-600 border border-sage-400/40 hover:bg-sage-400/25"
            : "text-stoneink-500 hover:bg-sand-200"
        }`}>
        <Gift size={12} weight={u.tier_source === "comp" ? "fill" : "regular"} />
        {u.tier_source === "comp" ? "Comp" : "Comp"}
      </button>
      {open && (
        <div className="absolute right-0 top-9 z-20 w-72 rounded-xl border border-sand-300 bg-white shadow-hover p-4 text-left" data-testid={`users-comp-panel-${u.id}`}>
          <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-terracotta-600 mb-2">
            Comp tier for {u.email}
          </div>
          <label className="text-xs text-stoneink-700 block mb-1">Tier</label>
          <div className="grid grid-cols-2 gap-2 mb-3">
            {["pro","family"].map((t) => (
              <button key={t} type="button" onClick={() => setTier(t)}
                className={`h-9 rounded-lg text-xs font-medium border ${
                  tier === t
                    ? "bg-terracotta-500 text-white border-terracotta-500"
                    : "bg-white text-stoneink-700 border-sand-300 hover:bg-sand-100"
                }`}>{t === "pro" ? "Pro" : "Family"}</button>
            ))}
          </div>
          <label className="text-xs text-stoneink-700 block mb-1">Duration</label>
          <div className="grid grid-cols-4 gap-1.5 mb-3">
            {[["30","30d"],["90","90d"],["365","1y"],["lifetime","♾"]].map(([v,l]) => (
              <button key={v} type="button" onClick={() => setDuration(v)}
                className={`h-9 rounded-lg text-xs font-medium border ${
                  duration === v
                    ? "bg-stoneink-900 text-white border-stoneink-900"
                    : "bg-white text-stoneink-700 border-sand-300 hover:bg-sand-100"
                }`}>{l}</button>
            ))}
          </div>
          <label className="text-xs text-stoneink-700 block mb-1">Note (optional)</label>
          <input type="text" value={note} onChange={(e) => setNote(e.target.value)}
            placeholder="e.g. influencer, friend, support comp"
            className="w-full px-3 h-9 rounded-lg bg-sand-100 border border-sand-300 text-xs focus:border-terracotta-500 outline-none mb-3" />
          <div className="flex gap-2">
            <button onClick={() => submit()} disabled={busy}
              data-testid={`users-comp-grant-${u.id}`}
              className="flex-1 h-9 rounded-lg bg-terracotta-600 hover:bg-terracotta-700 text-white text-xs font-medium disabled:opacity-50">
              {busy ? "…" : "Grant"}
            </button>
            {u.tier_source === "comp" && (
              <button onClick={() => submit("free")} disabled={busy}
                data-testid={`users-comp-revoke-${u.id}`}
                className="h-9 px-3 rounded-lg border border-destructive/40 text-destructive text-xs font-medium hover:bg-destructive/10">
                Revoke
              </button>
            )}
            <button onClick={() => setOpen(false)} className="h-9 px-3 rounded-lg text-stoneink-500 text-xs hover:bg-sand-100">Cancel</button>
          </div>
        </div>
      )}
    </span>
  );
}

const fmt = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString(); } catch { return "—"; }
};

function DemoControlPanel() {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState("");

  const refresh = async () => {
    try {
      const { data } = await api.get("/admin/demo/status");
      setStatus(data);
    } catch (err) {
      // 404 or unauthorized — quietly hide
      setStatus({ exists: false });
    }
  };

  useEffect(() => { refresh(); }, []);

  const onSnapshot = async () => {
    if (!window.confirm("Save the current state of the demo account as the new template?\n\nFrom now on, nightly resets will restore the demo back to exactly what's in it right now (items, locations, categories, photos).")) return;
    setBusy("snapshot");
    try {
      const { data } = await api.post("/admin/demo/snapshot");
      toast.success(`Snapshot saved · ${data.counts.items} items, ${data.counts.locations} locations, ${data.counts.categories} categories`);
      refresh();
    } catch (err) {
      toast.error(formatError(err) || "Snapshot failed");
    } finally {
      setBusy("");
    }
  };

  const onReset = async () => {
    if (!status?.snapshot) {
      toast.error("Save a snapshot first — there's nothing to restore to yet.");
      return;
    }
    if (!window.confirm("Reset the demo account now?\n\nThis deletes everything testers have added and restores from the saved snapshot. The nightly job runs at 03:00 UTC anyway.")) return;
    setBusy("reset");
    try {
      const { data } = await api.post("/admin/demo/reset");
      if (data.status === "no_template") {
        toast.error("No snapshot template found. Click 'Save snapshot' first.");
      } else {
        toast.success(`Demo reset · removed ${data.removed.items} items, restored ${data.restored.items} items`);
      }
      refresh();
    } catch (err) {
      toast.error(formatError(err) || "Reset failed");
    } finally {
      setBusy("");
    }
  };

  if (!status) return null;
  if (!status.exists) {
    return (
      <div className="rounded-2xl border border-dashed border-sand-300 bg-sand-100 p-5 text-sm text-stoneink-700" data-testid="demo-panel-missing">
        Demo account not provisioned yet. Set <code className="bg-white px-1.5 py-0.5 rounded">DEMO_ACCOUNT_EMAIL</code> env and redeploy — the backend will auto-create it on startup.
      </div>
    );
  }

  const snap = status.snapshot;
  const live = status.live || {};
  return (
    <div className="rounded-3xl border border-sand-300 bg-white p-5 sm:p-6 shadow-ambient" data-testid="demo-control-panel">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.2em] text-sage-600 mb-1">
            <Sparkle weight="duotone" size={12} /> Demo account · {status.email}
          </div>
          <h3 className="font-heading text-xl text-stoneink-900 font-semibold">Snapshot &amp; reset controls</h3>
          <p className="mt-1 text-sm text-stoneink-500 max-w-xl">
            Curate the demo by signing in as <code className="bg-sand-100 px-1.5 py-0.5 rounded text-xs">{status.email}</code> and adding the rooms / items / photos you want testers to see, then click <strong>Save snapshot</strong>. Every night at 03:00 UTC the demo resets back to that snapshot.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={onSnapshot}
            disabled={!!busy}
            data-testid="demo-panel-snapshot-btn"
            className="h-10 px-4 rounded-xl bg-terracotta-500 hover:bg-terracotta-600 disabled:opacity-50 text-white text-sm font-semibold flex items-center gap-2"
          >
            <Sparkle weight="fill" size={14} /> {busy === "snapshot" ? "Saving…" : (snap ? "Re-save snapshot" : "Save snapshot")}
          </button>
          <button
            onClick={onReset}
            disabled={!!busy || !snap}
            data-testid="demo-panel-reset-btn"
            className="h-10 px-4 rounded-xl bg-sage-400/15 border border-sage-400/40 text-stoneink-900 hover:bg-sage-400/25 disabled:opacity-40 disabled:cursor-not-allowed text-sm font-medium flex items-center gap-2"
            title={!snap ? "Save a snapshot first" : ""}
          >
            <ArrowClockwise size={14} /> {busy === "reset" ? "Resetting…" : "Reset now"}
          </button>
        </div>
      </div>

      <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
        <div className="rounded-2xl border border-sand-300 bg-sand-100/60 p-4">
          <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-stoneink-500 mb-1">Live demo right now</div>
          <div className="font-heading text-2xl text-stoneink-900 font-semibold">{live.items ?? 0} items</div>
          <div className="text-stoneink-500 mt-0.5">{live.locations ?? 0} locations · {live.categories ?? 0} categories</div>
        </div>
        <div className="rounded-2xl border border-sand-300 bg-sand-100/60 p-4">
          <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-stoneink-500 mb-1">Saved snapshot</div>
          {snap ? (
            <>
              <div className="font-heading text-2xl text-stoneink-900 font-semibold">{snap.counts.items} items</div>
              <div className="text-stoneink-500 mt-0.5">{snap.counts.locations} locations · {snap.counts.categories} categories · saved {new Date(snap.captured_at).toLocaleString()}</div>
            </>
          ) : (
            <>
              <div className="font-heading text-2xl text-terracotta-600 font-semibold">No snapshot yet</div>
              <div className="text-stoneink-500 mt-0.5">Reset is disabled until you save one.</div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
